import React, { useMemo, useState } from 'react';
import {
  BookOpen,
  Search,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  HelpCircle,
  RefreshCw,
  CheckCircle2,
  Clock,
  Edit3,
  MessageSquare,
  History,
  MoreHorizontal,
  // Type icons (matches Pulse_Base EntryIcon)
  ClipboardList,
  ListChecks,
  GraduationCap,
  Scale,
  ShieldCheck,
} from 'lucide-react';
import { WIKI_CATEGORIES, WIKI_PAGES } from '../../mock_data/wiki/wikiPages';

const WIKI_TITLE = 'Content library';
const WIKI_DESCRIPTION = 'SOPs, training, policies, and protocols.';

// Per-entry type icons — mirror of Pulse_Base/pulse_app_next EntryIcon.tsx.
const TYPE_ICON = {
  sop:       ClipboardList,
  checklist: ListChecks,
  training:  GraduationCap,
  policy:    Scale,
  protocol:  ShieldCheck,
};

const TYPE_LABEL = {
  sop: 'SOP',
  checklist: 'Checklist',
  training: 'Training',
  policy: 'Policy',
  protocol: 'Protocol',
};

const SIZE_CLASS = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
  xl: 'w-6 h-6',
};

const PAGES_PER_CATEGORY = 6;

function timeAgo(iso) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function Avatar({ owner }) {
  return (
    <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
      {owner.initials}
    </div>
  );
}

function EntryIcon({ entry, size = 'md', className = '' }) {
  const Icon = TYPE_ICON[entry.type] || ClipboardList;
  return <Icon className={`${SIZE_CLASS[size]} text-slate-500 ${className}`} />;
}

function TypeBadge({ type }) {
  const Icon = TYPE_ICON[type] || ClipboardList;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700">
      <Icon className="w-3 h-3" />
      {TYPE_LABEL[type] || type}
    </span>
  );
}

function StatusBadge({ status = 'published' }) {
  const styles = {
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    draft:     'bg-amber-50 text-amber-700 border-amber-200',
    archived:  'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wide ${styles[status] || styles.published}`}>
      {status}
    </span>
  );
}

// ── Home view: 2-column grid of category sections ───────────────────────
function HomeView({ entries, onSelect }) {
  const sections = useMemo(() => {
    return WIKI_CATEGORIES.map((cat) => {
      const inCat = entries
        .filter((e) => e.category === cat.id)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      return {
        key: cat.id,
        title: cat.name,
        description: cat.description,
        entries: inCat.slice(0, PAGES_PER_CATEGORY),
        totalAvailable: inCat.length,
      };
    }).filter((s) => s.entries.length > 0);
  }, [entries]);

  if (sections.length === 0) {
    return (
      <div className="px-4 md:px-8 py-16 text-center text-sm text-slate-400">
        Nothing matches the current filter. Clear the search above, or switch to{' '}
        <strong>All pages</strong> to browse the full list.
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-8 max-w-5xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        {sections.map((s) => (
          <section key={s.key}>
            <header className="border-b border-slate-200 pb-1.5 mb-2 flex items-baseline justify-between gap-2">
              <h2 className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
                {s.title}
              </h2>
              <span className="text-[11px] text-slate-400 tabular-nums">
                {s.totalAvailable > s.entries.length
                  ? `${s.entries.length} of ${s.totalAvailable}`
                  : s.entries.length}
              </span>
            </header>
            {s.description && (
              <p className="text-xs text-slate-500 -mt-1 mb-2">{s.description}</p>
            )}
            <ul className="space-y-1.5">
              {s.entries.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-2 group">
                  <button
                    onClick={() => onSelect(e.id)}
                    className="inline-flex items-start gap-2 text-sm text-slate-800 hover:text-brand-700 text-left min-w-0 flex-1"
                  >
                    <span className="mt-0.5 flex-shrink-0">
                      <EntryIcon entry={e} size="lg" />
                    </span>
                    <span className="underline decoration-slate-300 group-hover:decoration-brand-500 underline-offset-2 leading-snug">
                      {e.title}
                    </span>
                    {e.verified && (
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0 mt-1" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

// ── All Pages view: table grouped by category ───────────────────────────
function Th({ children, sortKey, current, dir, onClick, first }) {
  const active = sortKey && current === sortKey;
  const handle = () => sortKey && onClick(sortKey);
  return (
    <th
      onClick={sortKey ? handle : undefined}
      className={`text-left px-3 py-2 border-b border-slate-200 ${first ? 'pl-4' : ''} ${
        sortKey ? 'cursor-pointer select-none hover:text-slate-700' : ''
      }`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey && (
          active
            ? (dir === 'asc'
                ? <ArrowUp className="w-3 h-3" />
                : <ArrowDown className="w-3 h-3" />)
            : <ChevronsUpDown className="w-3 h-3 opacity-40" />
        )}
      </span>
    </th>
  );
}

function Td({ children, first, className = '' }) {
  return (
    <td className={`px-3 py-2 border-b border-slate-100 ${first ? 'pl-4' : ''} ${className}`}>
      {children}
    </td>
  );
}

function AllPagesView({ entries, sortKey, sortDir, onSort, onSelect }) {
  const [collapsed, setCollapsed] = useState(() => new Set());

  const groups = useMemo(() => {
    const byCat = new Map();
    for (const e of entries) {
      if (!byCat.has(e.category)) byCat.set(e.category, []);
      byCat.get(e.category).push(e);
    }
    return WIKI_CATEGORIES
      .filter((c) => byCat.has(c.id))
      .map((c) => {
        const list = byCat.get(c.id) || [];
        const sorted = [...list].sort((a, b) => {
          let cmp = 0;
          if (sortKey === 'title') cmp = a.title.localeCompare(b.title);
          else if (sortKey === 'updated') cmp = new Date(a.updatedAt) - new Date(b.updatedAt);
          return sortDir === 'asc' ? cmp : -cmp;
        });
        return { key: c.id, label: c.name, entries: sorted };
      });
  }, [entries, sortKey, sortDir]);

  const toggle = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-separate border-spacing-0">
        <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <Th first sortKey="title" current={sortKey} dir={sortDir} onClick={onSort}>Page</Th>
            <Th>Tags</Th>
            <Th>Owner</Th>
            <Th>Section</Th>
            <Th sortKey="updated" current={sortKey} dir={sortDir} onClick={onSort}>Last updated</Th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isCollapsed = collapsed.has(g.key);
            return (
              <React.Fragment key={g.key}>
                <tr className="bg-slate-50">
                  <td colSpan={5} className="px-3 py-1.5 border-b border-slate-200">
                    <button
                      onClick={() => toggle(g.key)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
                    >
                      {isCollapsed
                        ? <ChevronRight className="w-3.5 h-3.5" />
                        : <ChevronDown className="w-3.5 h-3.5" />}
                      <span>{g.label}</span>
                      <span className="text-slate-400 font-normal">{g.entries.length}</span>
                    </button>
                  </td>
                </tr>
                {!isCollapsed &&
                  g.entries.map((e) => (
                    <tr
                      key={e.id}
                      onClick={() => onSelect(e.id)}
                      className="cursor-pointer hover:bg-brand-50/60 transition-colors"
                    >
                      <Td first>
                        <div className="flex items-center gap-2 min-w-0">
                          <EntryIcon entry={e} size="md" />
                          <span className="font-medium text-slate-900 truncate">{e.title}</span>
                          {e.verified && (
                            <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1 flex-wrap">
                          {(e.tags || []).slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Avatar owner={e.owner} />
                          <span className="text-xs text-slate-600 truncate">{e.owner.name}</span>
                        </div>
                      </Td>
                      <Td>
                        <span className="text-xs text-slate-500 capitalize">{e.category}</span>
                      </Td>
                      <Td className="text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeAgo(e.updatedAt)}
                        </span>
                      </Td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
          {groups.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-12 text-center text-slate-400 text-sm">
                No pages match the current filter.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Detail view — mirrors Pulse_Base EntryDetail layout ──────────────────
function EntryDetail({ entry, onBack }) {
  return (
    <article className="h-full flex flex-col bg-white">
      {/* Toolbar — Back · badges · Comments / History · More · Edit */}
      <header className="px-4 md:px-6 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 flex-shrink-0"
            title="Back to library"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Library
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <TypeBadge type={entry.type} />
            <StatusBadge status="published" />
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5"
            title="Comments"
          >
            <MessageSquare className="w-3.5 h-3.5" /> Comments
          </button>
          <button
            className="px-2.5 py-1.5 text-xs text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5"
            title="Version history"
          >
            <History className="w-3.5 h-3.5" /> History
          </button>
          <button
            className="px-2 py-1.5 text-xs text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center"
            title="More actions"
            aria-haspopup="menu"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          <button
            className="group relative inline-flex items-center justify-center w-9 h-9 rounded-md shadow-sm transition bg-brand-500 hover:bg-brand-600 text-white"
            title="Edit"
            aria-label="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto px-4 md:px-10 py-6 md:py-10 max-w-5xl">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 flex items-center gap-3">
            <EntryIcon entry={entry} size="xl" className="text-slate-700 flex-shrink-0" />
            <span>{entry.title}</span>
          </h1>

          {entry.summary && (
            <p className="text-base text-slate-600 mb-5">{entry.summary}</p>
          )}

          {/* Owner + metadata row */}
          <div className="mt-2 mb-5 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Avatar owner={entry.owner} />
              <span>{entry.owner.name}</span>
            </div>
            <span className="text-slate-300">·</span>
            <span className="capitalize">{entry.category}</span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {formatDateTime(entry.updatedAt)}
            </span>
            {entry.verified && (
              <>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1 text-emerald-600">
                  <CheckCircle2 className="w-3 h-3" />
                  Verified
                </span>
              </>
            )}
          </div>

          {entry.tags?.length > 0 && (
            <div className="mb-6 flex items-center gap-1.5 flex-wrap">
              {entry.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className="border-t border-slate-100 pt-6">
            <article
              className="wiki-prose text-sm md:text-base text-slate-700 leading-relaxed [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-2 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_ul]:ml-5 [&_ol]:ml-5 [&_li]:my-0.5 [&_ul]:list-disc [&_ol]:list-decimal [&_strong]:text-slate-900 [&_em]:text-slate-500"
              dangerouslySetInnerHTML={{ __html: entry.body }}
            />
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Wiki() {
  const [view, setView] = useState('home'); // 'home' | 'all'
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [sortKey, setSortKey] = useState('updated');
  const [sortDir, setSortDir] = useState('desc');

  const filtered = useMemo(() => {
    if (!search) return WIKI_PAGES;
    const q = search.toLowerCase();
    return WIKI_PAGES.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q)) ||
        p.owner.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
    );
  }, [search]);

  const selected = useMemo(
    () => WIKI_PAGES.find((p) => p.id === selectedId) || null,
    [selectedId]
  );

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="page-wrapper flex flex-col h-full bg-slate-50/40 min-h-screen">
      {/* ── Top header ─────────────────────────────────────────────────
          Hidden while viewing a single page so the EntryDetail's own
          toolbar can take over without competing chrome. */}
      {!selected && (
        <div className="bg-white border-b border-slate-200 px-4 md:px-8 py-3 flex items-center gap-3 flex-shrink-0">
          <BookOpen className="w-5 h-5 text-slate-600 flex-shrink-0" />
          <h1 className="text-lg md:text-xl font-bold text-slate-900 truncate">
            {WIKI_TITLE}
          </h1>
          <span className="hidden md:inline text-sm text-slate-500 truncate">
            · {WIKI_DESCRIPTION}
          </span>

          <div className="flex-1" />

          <div className="inline-flex border border-slate-200 rounded-md overflow-hidden text-xs">
            {[
              { id: 'home', label: 'Home' },
              { id: 'all',  label: 'All pages' },
            ].map((opt) => {
              const active = view === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setView(opt.id)}
                  className={`px-3 py-1.5 font-medium transition-colors ${
                    active ? 'bg-brand-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pages, tags, owners…"
              className="pl-8 pr-3 py-1.5 w-64 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>

          <button
            className="p-1.5 text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            className="p-1.5 text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
            title="How the wiki works"
            aria-label="Help"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────────── */}
      <main className="flex-1 min-h-0 overflow-auto">
        {selected ? (
          <EntryDetail entry={selected} onBack={() => setSelectedId(null)} />
        ) : view === 'home' ? (
          <HomeView entries={filtered} onSelect={setSelectedId} />
        ) : (
          <AllPagesView
            entries={filtered}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            onSelect={setSelectedId}
          />
        )}
      </main>
    </div>
  );
}
